import FormService from '../../../core/services/FormService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired } from '../../../core/validations';
import { action, extendObservable } from 'mobx';
import { get } from 'lodash';
import FetchService from '../../../core/services/FetchService';
import { CONTRACT_PRODUCTS } from '../../../core/consts';
import { toJS } from 'mobx';

export default class ContractProductsService {
  form = new FormService();
  fetch = new FetchService();

  constructor() {
    extendObservable(this, {
      type: '',
      products: [],
      isModalOpen: false,
      isEditing: false,
      contractProducts: [],
      isV2: false,
    });
    this.form.validations = {
      hs_sku: [isRequired],
      finalUnitPrice: [isRequired],
    };
  }

  reset = action(() => {
    this.fetch = new FetchService();
    this.type = '';
    this.products = [];
    this.isModalOpen = false;
    this.isEditing = false;
    this.contractProducts = [];
    this.isV2 = false;
    this.form.setInitialValues({
      isDebitProduct: false,
    });
    this.form.reset();
  });

  init = action((type, isEditing = false) => {
    // old method
    // this.type = type;
    // this.isEditing = isEditing;
    // this.contractProducts = [];
    // this.form.setInitialValues({
    //   isDebitProduct: false,
    // });
    // this.form.reset();
    // await this.load();

    this.reset();
    this.type = type;
    this.isEditing = isEditing;
  });

  load = action(async () => {
    if (!!this.type) {
      await this.fetch.fetch({
        method: 'get',
        url: `/contracts/${this.type}/products`,
        apiVersion: 'v3',
      });

      if (this.fetch.data) {
        if (this.type === 'UPFRONT') {
          this.products =
            this.fetch.data
              ?.map((item) => ({
                label: item.description,
                value: item.hs_sku,
                disabled: !item.hs_sku,
                group: item.group,
                description: item.description,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)) || [];

          if (!this.isEditing) {
            this.contractProducts =
              this.fetch.data
                ?.sort((a, b) => a.order - b.order)
                .filter((item) => item.hs_sku !== CONTRACT_PRODUCTS.OTHERS)
                .map((item, index) => ({
                  ...item,
                  finalUnitPrice: Number(item.unitPrice),
                  isEnabled: true,
                  rowKey: index + 1,
                })) || [];
          }

          if (this.isEditing && this.isV2) {
            this.contractProducts = [
              ...toJS(this.contractProducts),
              ...(this.fetch.data
                ?.sort((a, b) => a.order - b.order)
                .filter(
                  (item) =>
                    !this.contractProducts.map((cp) => cp.hs_sku).includes(item.hs_sku) &&
                    item.hs_sku !== CONTRACT_PRODUCTS.OTHERS
                )
                .map((item, index) => ({
                  ...item,
                  finalUnitPrice: Number(item.unitPrice),
                  isEnabled: false,
                  rowKey: index + 1,
                })) || []),
            ];
          }
        }

        if (this.type === 'POST_PAYMENT') {
          this.products =
            this.fetch.data
              ?.map((item) => ({
                label: item.description,
                value: item.hs_sku,
                disabled: !item.hs_sku,
                group: item.group,
                description: item.description,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)) || [];
        }

        if (
          !!this.contractProducts.length &&
          this.contractProducts.some((cp) => this.products.map((p) => p.value).includes(cp.hs_sku))
        ) {
          this.products = [
            ...toJS(this.products),
            ...this.contractProducts
              .filter((cp) => !this.products.map((p) => p.value).includes(cp.hs_sku))
              .map((item) => ({
                label: item.description,
                value: item.hs_sku,
                disabled: !item.hs_sku,
                group: item.group,
                description: item.description,
              })),
          ]
            .filter((product) => ![CONTRACT_PRODUCTS.AI_EXERCISES].includes(product.value))
            .sort((a, b) => {
              if (a.order && b.order) {
                return a.order - b.order;
              }
              return a.label.localeCompare(b.label);
            });
        }
      }
    }
  });

  handleSubmit = action(() => {
    const { min, max, rangePrice, ...productFormValues } = toJS(this.form.getValues());
    if (
      [CONTRACT_PRODUCTS.EXTRA_LICENSES_RANGE, CONTRACT_PRODUCTS.EXTRA_LICENSES_SEAT_RANGE].includes(
        productFormValues.hs_sku
      )
    ) {
      this.form.validations.finalUnitPrice = [];
    }
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    const product = {
      ...productFormValues,
      description: (() => {
        let description = '';
        if (this.form.getValue('hs_sku') === 'others' && this.form.getValue('description')) {
          description = this.form.getValue('description');
        } else if (this.form.getValue('description')) {
          description = this.form.getValue('description');
        } else {
          description = get(
            this.products.find((product) => product.value === get(this.form.getValues(), 'hs_sku', null)),
            'label'
          );
        }

        return description;
      })(),
      group:
        get(
          this.products.find((product) => product.value === get(this.form.getValues(), 'hs_sku', null)),
          'group'
        ) || null,
      isDebitProduct: this.type === 'UPFRONT',
      isEnabled: true,
    };
    if (
      [CONTRACT_PRODUCTS.EXTRA_LICENSES_RANGE, CONTRACT_PRODUCTS.EXTRA_LICENSES_SEAT_RANGE].includes(
        productFormValues.hs_sku
      ) &&
      (!productFormValues.finalUnitPriceRanges || productFormValues.finalUnitPriceRanges.length < 2)
    ) {
      NotificationService.addNotification('The product must contain at least two ranges.', 'error');
      return;
    }
    if (
      this.contractProducts
        .filter((p) => p.rowKey !== product.rowKey)
        .some((p) => p.hs_sku === product.hs_sku && p.hs_sku !== 'others')
    ) {
      NotificationService.addNotification('Product already added', 'error');
      return;
    }
    const hasProductsWithSameGroup = this.contractProducts
      .filter((addedProduct) => !this.isEditing || (this.isEditing && addedProduct.rowKey !== product.rowKey))
      .some((addedProduct) => !!addedProduct.group && !!product.group && addedProduct.group === product.group);
    if (hasProductsWithSameGroup) {
      NotificationService.addNotification('Its allowed to add only one product from each group', 'error');
      return;
    }

    if (
      product.installments &&
      product.paidInstallments &&
      Number(product.installments) < Number(product.paidInstallments)
    ) {
      NotificationService.addNotification(
        'The number of paid installments must be less than or equal to the number of installments.',
        'error'
      );
      return;
    }

    if (this.form.getValue('rowKey')) {
      this.contractProducts = this.contractProducts.map((p) => {
        if (p.rowKey === product.rowKey) {
          return product;
        }
        return p;
      });
    } else {
      this.contractProducts = [...this.contractProducts, { ...product, rowKey: this.contractProducts.length + 1 }];
    }
    this.form.setInitialValues({});
    this.form.reset();
    this.isModalOpen = false;
    this.isEditing = false;
  });

  handleEdit = ({ rowKey }) => {
    const product = this.contractProducts.find((product) => product.rowKey === rowKey);
    this.form.setInitialValues(product);
    this.isModalOpen = true;
    this.isEditing = true;
  };

  handleToggleProduct = action((product) => {
    this.contractProducts = this.contractProducts.map((p) => {
      if (p.rowKey === product.rowKey) {
        return { ...p, isEnabled: !p.isEnabled };
      }
      return p;
    });
  });

  handleDelete = (productToDelete) => {
    ConfirmationDialogService.show(
      'Delete Product',
      `Do you really want to delete product ${productToDelete.description}?`,
      () => {
        this.contractProducts = this.contractProducts
          .filter((product) => product.rowKey !== productToDelete.rowKey)
          .map((product, index) => {
            const currentProduct = { ...product };
            currentProduct.rowKey = index + 1;
            return currentProduct;
          });
      }
    );
  };

  handleToggleModal = () => {
    if (this.type) {
      this.form.reset();
      this.isModalOpen = !this.isModalOpen;
      this.isEditing = false;
    } else {
      NotificationService.addNotification('Before adding a product, you must select the contract type.', 'error');
    }
  };

  handleAddRange = () => {
    let { min, max, rangePrice, finalUnitPriceRanges, ...form } = toJS(this.form.getValues());
    min = Number(min);
    max = Number(max);
    rangePrice = Number(rangePrice);
    if (!min || !max || !rangePrice) {
      NotificationService.addNotification('The min, max and price is required.', 'error');
      return;
    }
    if (min < 0 || max < 0 || rangePrice < 1) {
      NotificationService.addNotification('The min, max and price must be valid numbers.', 'error');
      return;
    }
    if (min > max) {
      NotificationService.addNotification('The minimum value cannot be greater than the maximum value.', 'error');
      return;
    }
    if (finalUnitPriceRanges && !!finalUnitPriceRanges.length) {
      const hasOverlap = finalUnitPriceRanges.some(
        (otherRange) =>
          (min >= otherRange.min && min <= otherRange.max) || (max >= otherRange.min && max <= otherRange.max)
      );
      if (hasOverlap) {
        NotificationService.addNotification('Ranges cannot overlap.', 'error');
        return;
      }
    }
    const ranges = finalUnitPriceRanges
      ? [...finalUnitPriceRanges, { min, max, price: rangePrice }]
      : [{ min, max, price: rangePrice }];
    this.form.setValues({
      ...form,
      finalUnitPriceRanges: ranges.sort((a, b) => a.min - b.min),
    });
  };

  handleDeleteRange = (range) => {
    const ranges = this.form.getValue('finalUnitPriceRanges');
    this.form.setValue(
      'finalUnitPriceRanges',
      ranges.filter((r) => r.min !== range.min && r.max !== range.max)
    );
  };
}
